// Production calendar on src/ui (DESIGN rev 2.1 §3.1: Calendar cell on a month / week frame; the Script list on Grid
// at medium density; Tabs). A UI migration: every call, gate and ticket of the old page stays.
//
// Calls (unchanged): GET /api/live-production/shoot-calendar · GET /api/team-members · GET /api/talent (Admin, Power
// User, Producer only) · GET /api/talent-availability/date-counts and /by-date (talent pool roles only) · PATCH and
// DELETE /api/shoot-segments/:id · PATCH /api/live-production/:id (assign talent) · POST /api/live-production/reorder ·
// Dropped because the old page never reached them either (handlers wired to nothing): POST /api/shoot-segments/reorder
// and PATCH /api/live-production/:id { shootTimeSlot }.
// Script list (pages/calendar/ScriptList.jsx): GET /api/tal, PATCH /api/tal/:id/remove | restore, PATCH
// /api/tal/lp/:id/notes, the schedule plan (components/SchedulePlanModal.jsx). Talent calendar
// (pages/calendar/TalentCalendar.jsx): GET /api/talent-availability/by-range, GET /api/shoot-segments?from&to and
// ?talentId&shootDay, GET + POST + DELETE /api/talent-bookings, GET + PUT /api/tal/day-notes, GET
// /api/tal/day-matches, GET /api/talent-availability(?talentId) and its POST / PATCH / DELETE.
//
// Gates (HD-6 step 0d): the tab set and every /api/tal read need canReadScriptList (never an external account) and a
// tab-bar role; /api/talent needs canAssignTalent; a role without the Script list lands on the Shoot schedule.
// Tickets: location.state.calendarInit { pageTab, scriptListTab } (the script editor's back button),
// scriptListFocus { tab, scriptId, liveProductionId } (the Production page), openSchedulerFor (a production id).
// localStorage: cal_view (month / week / day), sl_client_v2 (the Script list's project).
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Button from '../ui/Button';
import Skeleton from '../ui/Skeleton';
import Tabs from '../ui/Tabs';
import { useToast } from '../ui/Toast';
import { usePageHeader } from '../shell/pageHeader';
import { clean } from '../shell/copy';
import SchedulerPanel from '../components/SchedulerPanel';
import TalentAvailabilityView from '../components/TalentAvailabilityView';
import Modal from '../ui/Modal';
import ScriptListView from './calendar/ScriptList';
import TalentCalendarView from './calendar/TalentCalendar';
import { DayDetailDialog, DayView, MonthView, TalentPool, WeekView } from './calendar/ShootSchedule';
import { canAssignTalent, canReadScriptList, canReorderShoots, canSeeTalentAvailability, plural } from './calendar/util';

const VIEWS = [{ key: 'month', label: 'Month' }, { key: 'week', label: 'Week' }, { key: 'day', label: 'Day' }];

// Tab order mirrors the workflow (owner call 2026-09-01): a Script list, then who is available, then the Shoot
// schedule. The Script list is the landing tab for the same reason.
const PAGE_TABS = [
  { id: 'assign', label: 'Script list' },
  { id: 'talent', label: 'Talent calendar' },
  { id: 'shoot',  label: 'Shoot schedule' },
];

export default function Calendar() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  // External accounts can hold Writer, but the API refuses them every /api/tal and /api/talent read.
  const showTalentPool = canSeeTalentAvailability(user) && canReadScriptList(user);
  const assignTalent   = canAssignTalent(user);

  // Return trip from the script editor: it navigates here with { calendarInit } so the user lands on the tab they left.
  const calendarInit = location.state?.calendarInit || null;

  const [pageTab,          setPageTab]          = useState(showTalentPool ? (calendarInit?.pageTab || 'assign') : 'shoot');
  const [display,          setDisplay]          = useState(() => localStorage.getItem('cal_view') || 'month');
  const [shootRecords,     setShootRecords]     = useState([]);
  const [allTalent,        setAllTalent]        = useState([]);
  const [loading,          setLoading]          = useState(false);
  const [selectedDay,      setSelectedDay]      = useState(null);
  const [talentForDay,     setTalentForDay]     = useState([]);
  const [talentLoading,    setTalentLoading]    = useState(false);
  const [talentCounts,     setTalentCounts]     = useState({}); // { 'YYYY-MM-DD': count }
  const talentCountsKey = useRef(null);                         // the last range fetched
  const [dayDetailDate,    setDayDetailDate]    = useState(null);
  const [schedulerProject, setSchedulerProject] = useState(null);
  const [availabilityTalent, setAvailabilityTalent] = useState(null); // staff editing a talent's availability: { id, name }

  // Production page ticket: open the Shoot scheduler for that record once the shoot records load, then consume it.
  useEffect(() => {
    const id = location.state?.openSchedulerFor;
    if (!id || !shootRecords?.length) return;
    const rec = shootRecords.find(r => r.id === id);
    if (rec) setSchedulerProject(rec);
    navigate('/calendar', { replace: true, state: {} });
  }, [location.state, shootRecords]); // eslint-disable-line react-hooks/exhaustive-deps

  // D-088: the Production page hands over { calendarInit, scriptListFocus } so the Script list opens on that
  // production's row. The ticket is consumed on mount.
  const [scriptListFocus, setScriptListFocus] = useState(location.state?.scriptListFocus || null);
  useEffect(() => {
    if (location.state?.scriptListFocus) navigate('/calendar', { replace: true, state: {} });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { localStorage.setItem('cal_view', display); }, [display]);

  const loadShoot = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/live-production/shoot-calendar');
      setShootRecords(res.data);
    } catch { /* the calendar stays as it was */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadShoot(); }, [loadShoot]);
  // Scheduling happens on the other tabs, so re-pull the shoot records whenever the Shoot schedule opens.
  useEffect(() => { if (pageTab === 'shoot') loadShoot(); }, [pageTab, loadShoot]);
  // Kept for call parity with the old page, which loaded the team list here (its result was never shown).
  useEffect(() => { api.get('/api/team-members').catch(() => {}); }, []);
  useEffect(() => {
    if (!showTalentPool || !assignTalent) return;   // GET /api/talent: Admin, Power User, Producer
    api.get('/api/talent').then(r => setAllTalent(r.data)).catch(() => {});
  }, [showTalentPool, assignTalent]);

  // Talent availability counts for the month's cells
  const loadTalentCounts = useCallback(async (from, to) => {
    if (!showTalentPool) return;
    const key = `${from}|${to}`;
    if (talentCountsKey.current === key) return;
    talentCountsKey.current = key;
    try {
      const res = await api.get(`/api/talent-availability/date-counts?from=${from}&to=${to}`);
      setTalentCounts(res.data);
    } catch { /* no counts */ }
  }, [showTalentPool]);

  // Talent available on the selected day (the Talent pool, the hour grid's conflict check)
  const loadTalentForDay = useCallback(async (dateStr) => {
    if (!showTalentPool || !dateStr) return;
    setTalentLoading(true);
    setTalentForDay([]);
    try {
      const res = await api.get(`/api/talent-availability/by-date?date=${dateStr}`);
      setTalentForDay(res.data);
    } catch { /* no availability shown */ } finally { setTalentLoading(false); }
  }, [showTalentPool]);

  useEffect(() => {
    if (selectedDay) loadTalentForDay(selectedDay);
    else setTalentForDay([]);
  }, [selectedDay, loadTalentForDay]);

  // Drag a shoot to another day: PATCH that segment's shoot day.
  const handleMoveSegment = useCallback(async (segmentId, dateStr) => {
    setShootRecords(prev => prev.map(r => ({ ...r, shootSegments: (r.shootSegments || []).map(s => (s.id === segmentId ? { ...s, shootDay: dateStr } : s)) })));
    try {
      await api.patch(`/api/shoot-segments/${segmentId}`, { shootDay: dateStr });
    } catch {
      loadShoot();
      toast('The shoot did not move. Try again.', { tone: 'danger' });
    }
  }, [loadShoot, toast]);

  // Remove a shoot from its day: delete the segment, so the calendar and the Shoot scheduler stay in step.
  const handleUnscheduleSegment = useCallback(async (segmentId) => {
    setShootRecords(prev => prev.map(r => ({ ...r, shootSegments: (r.shootSegments || []).filter(s => s.id !== segmentId) })));
    try {
      await api.delete(`/api/shoot-segments/${segmentId}`);
    } catch {
      loadShoot();
      toast('The shoot was not removed. Try again.', { tone: 'danger' });
    }
  }, [loadShoot, toast]);

  // Drop an available talent on a month day: assign them to that day's first production
  const handleAssignTalent = useCallback(async (liveId, talentChip) => {
    if (!assignTalent) return;
    try {
      const res = await api.patch(`/api/live-production/${liveId}`, { talentId: talentChip.talentId });
      setShootRecords(prev => prev.map(r => (r.id === liveId ? { ...r, ...res.data } : r)));
    } catch {
      toast('The talent was not assigned. Try again.', { tone: 'danger' });
    }
  }, [assignTalent, toast]);

  const handleBulkReorder = useCallback(async (date, order) => {
    const orderMap = Object.fromEntries(order.map(({ id, shootOrder }) => [id, shootOrder]));
    setShootRecords(prev => prev.map(r => (orderMap[r.id] !== undefined ? { ...r, shootOrder: orderMap[r.id] } : r)));
    try {
      await api.post('/api/live-production/reorder', { order });
    } catch {
      loadShoot();
      toast('The new order did not save. Try again.', { tone: 'danger' });
    }
  }, [loadShoot, toast]);

  // Each Cam carries its own time slot
  const handleSegmentTimeSlot = useCallback(async (segmentId, timeSlot) => {
    setShootRecords(prev => prev.map(r => ({ ...r, shootSegments: (r.shootSegments || []).map(s => (s.id === segmentId ? { ...s, shootTimeSlot: timeSlot } : s)) })));
    try {
      await api.patch(`/api/shoot-segments/${segmentId}`, { shootTimeSlot: timeSlot });
    } catch {
      loadShoot();
      toast('The time did not save. Try again.', { tone: 'danger' });
    }
  }, [loadShoot, toast]);

  const openDayDetail = useCallback(key => { setDayDetailDate(key); setSelectedDay(key); }, []);

  // Awaiting a shoot date: an active production with no segments yet or a segment without a day. Approved scripts
  // only (D-053); Riff cards carry no script and count as before.
  const unscheduledCount = useMemo(() => shootRecords.filter(r => {
    if (!['Scripted', 'Filming', 'Incomplete'].includes(r.status)) return false;
    if (r.script && r.script.qualStatus !== 'Approved') return false;
    const segs = r.shootSegments || [];
    return segs.length === 0 || segs.some(s => !s.shootDay);
  }).length, [shootRecords]);

  const handleOpenCardById = useCallback(id => navigate(`/production/${id}`), [navigate]);
  const openRecord = useCallback(rec => navigate(`/production/${rec.id}`), [navigate]);

  // Productions with a shoot on the selected day (the drag hint in the Talent pool)
  const recordsOnSelectedDay = useMemo(() => {
    if (!selectedDay) return [];
    return shootRecords.filter(r => (r.shootSegments || []).some(s => s.shootDay?.slice(0, 10) === selectedDay));
  }, [shootRecords, selectedDay]);

  const dayDetailEntries = useMemo(() => (!dayDetailDate ? [] : shootRecords.flatMap(rec => (rec.shootSegments || [])
    .filter(s => s.shootDay?.slice(0, 10) === dayDetailDate)
    .map(segment => ({ segment, rec })))), [shootRecords, dayDetailDate]);

  usePageHeader({
    title: 'Production calendar',
    meta: pageTab === 'shoot'
      ? `Shoot schedule: ${plural(unscheduledCount, 'script')} awaiting a shoot date`
      : pageTab === 'talent' ? 'Talent availability and bookings' : 'Scripts ready to plan, and what is already assigned',
    actions: pageTab === 'shoot' ? (
      <>
        <Button variant="quiet" icon={RefreshCw} onClick={loadShoot}>Refresh</Button>
        <div className="ui-seg" role="group" aria-label="Calendar view">
          {VIEWS.map(v => <Button key={v.key} variant="quiet" aria-pressed={display === v.key} onClick={() => setDisplay(v.key)}>{v.label}</Button>)}
        </div>
      </>
    ) : null,
  });

  const closeAvailability = () => {
    setAvailabilityTalent(null);
    // Refresh the selected day's chips and force a counts refresh on the next month render
    if (selectedDay) loadTalentForDay(selectedDay);
    talentCountsKey.current = null;
  };

  return (
    <div className="ui ui-page ui-page--flush">
      {/* The tab bar is staff only (§5.1); write actions stay Producer / Power User / Admin and the server enforces them */}
      {showTalentPool && (
        <div className="ui-inset">
          <Tabs label="Production calendar" value={pageTab} onChange={setPageTab} items={PAGE_TABS} />
        </div>
      )}

      {pageTab === 'assign' && (
        <ScriptListView focus={scriptListFocus} onFocusConsumed={() => setScriptListFocus(null)}
          initialTab={calendarInit?.scriptListTab} onOpenCard={handleOpenCardById} onScheduled={loadShoot}
          onOpenShootDay={d => { setPageTab('shoot'); setDayDetailDate(d); setSelectedDay(d); }}
          onOpenTalentCalendar={() => setPageTab('talent')} />
      )}

      {pageTab === 'talent' && (
        <div className="ui-pane"><TalentCalendarView /></div>
      )}

      {pageTab === 'shoot' && (
        <div className="ui-pane ui-stack ui-stack--loose">
          {loading ? (
            <Skeleton variant="block" height={480} label="Loading the shoot schedule" />
          ) : display === 'week' ? (
            <WeekView records={shootRecords} onMoveSegment={handleMoveSegment} onUnscheduleSegment={handleUnscheduleSegment} onOpenVideoCard={openRecord} />
          ) : display === 'day' ? (
            <DayView shootRecords={shootRecords} onOpenVideoCard={openRecord} onReorder={handleBulkReorder} />
          ) : (
            <MonthView records={shootRecords} talentCounts={showTalentPool ? talentCounts : {}} canSelectDay={showTalentPool}
              onMoveSegment={handleMoveSegment} onUnscheduleSegment={handleUnscheduleSegment} onOpenVideoCard={openRecord}
              onLoadCounts={loadTalentCounts} onDaySelect={setSelectedDay} onAssignTalent={assignTalent ? handleAssignTalent : null}
              onDayDetailOpen={openDayDetail} />
          )}
          {!loading && showTalentPool && (
            <TalentPool selectedDay={selectedDay} talentLoading={talentLoading} talentForDay={talentForDay} allTalent={allTalent}
              assignTalent={assignTalent} dragHint={Boolean(selectedDay && !talentLoading && assignTalent && recordsOnSelectedDay.length > 0)}
              onEditTalent={setAvailabilityTalent} />
          )}
        </div>
      )}

      {schedulerProject && (
        <SchedulerPanel record={schedulerProject} allTalent={allTalent}
          // Refresh on close so segments changed in the panel show on the calendar behind it
          onClose={() => { setSchedulerProject(null); loadShoot(); }}
          onProjectUpdated={updated => {
            setShootRecords(prev => prev.map(r => (r.id === updated.id ? { ...r, ...updated } : r)));
            setSchedulerProject(prev => (prev?.id === updated.id ? { ...prev, ...updated } : prev));
          }} />
      )}

      {dayDetailDate && (
        <DayDetailDialog dateStr={dayDetailDate} entries={dayDetailEntries} talentForDay={talentForDay} canReorder={canReorderShoots(user)}
          onClose={() => setDayDetailDate(null)} onSegmentTimeSlot={handleSegmentTimeSlot}
          onOpenVideoCard={r => { setDayDetailDate(null); navigate(`/production/${r.id}`); }} />
      )}

      {availabilityTalent && (
        <Modal size="wide" title={`Availability: ${clean(availabilityTalent.name)}`} meta="Click any date to mark or edit availability for this talent."
          onClose={closeAvailability}>
          <TalentAvailabilityView talentId={availabilityTalent.id} />
        </Modal>
      )}
    </div>
  );
}
